import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import NotificationSnackbar from "../../../../common/notificationBar/notification-snackbar";

const TasksExportIncident = ({ filteredTasks }) => {
  const [openExportSnackbar, setExportOpenSnackbar] = useState(false);

  const renderTable = (doc, title, data, columns, startY = null) => {
    if (startY) doc.setPage(doc.internal.getNumberOfPages());

    doc.setFontSize(14);
    doc.text(title, 14, startY || doc.lastAutoTable?.finalY + 10 || 30);

    autoTable(doc, {
      head: [columns],
      body: data,
      startY: startY || doc.lastAutoTable?.finalY + 15,
    });
  };

  const formatDateSafe = (dateValue) => {
    try {
      const parsed = new Date(dateValue);
      if (!parsed || isNaN(parsed)) return "N/A";
      return format(parsed, "dd MMM yyyy HH:mm");
    } catch {
      return "N/A";
    }
  };

  const handleExportToPDF = () => {
    if (!filteredTasks) return;

    const doc = new jsPDF();
    const formattedDate = formatDateSafe(new Date());

    doc.setFontSize(18);
    doc.text("Incident Report Summary", 14, 20);

    const {
      incidentManagementInfo,
      witnessInfo,
      behaviouralInfo,
      challengingInfo,
      strategyInfo,
      physicalInfo,
      verbalWarningInfo,
      sound,
      temperature,
      whoWasAround,
      warningGiven,
      updated_at,
      staffDebrief,
      childDebrief,
      supportStatement,
    } = filteredTasks;

    // 1. Incident Management Section
    const incidentTable = [
      ["Updated At", formatDateSafe(updated_at)],
      ["Temperature", temperature],
      ["Sound", sound],
      ["Who Was Around", whoWasAround],
      ["Warning Given", warningGiven],
    ];

    autoTable(doc, {
      startY: 30,
      head: [],
      body: incidentTable,
      theme: "plain",
      styles: { fontSize: 12 },
    });

    // JSON block helper
    const flattenJSON = (jsonObj) =>
      Object.entries(jsonObj || {}).map(([key, value]) => {
        if (typeof value === "object" && value !== null) {
          try {
            return [key, JSON.stringify(value, null, 2)]; // Pretty-print JSON
          } catch {
            return [key, "[Invalid JSON]"];
          }
        } else {
          return [key, String(value)];
        }
      });

    renderTable(doc, "Incident Management Info", flattenJSON(incidentManagementInfo), ["Field", "Value"]);
    renderTable(doc, "Witness Info", flattenJSON(witnessInfo), ["Field", "Value"]);
    renderTable(doc, "Behavioural Info", flattenJSON(behaviouralInfo), ["Field", "Value"]);
    renderTable(doc, "Challenging Behaviour Info", flattenJSON(challengingInfo), ["Field", "Value"]);
    renderTable(doc, "De-escalation Strategy Info", flattenJSON(strategyInfo), ["Field", "Value"]);
    renderTable(doc, "Physical Intervention Info", flattenJSON(physicalInfo), ["Field", "Value"]);
    renderTable(doc, "Verbal Warning Info", flattenJSON(verbalWarningInfo), ["Field", "Value"]);

    // 2. Child Debrief
    if (childDebrief) {
      renderTable(
        doc,
        "Child Debrief - General Info",
        [
          ["Task Name", childDebrief.taskName],
          ["Gender", childDebrief.gender || "N/A"],
          ["Date", formatDateSafe(childDebrief.updated_at)],
        ],
        ["Field", "Value"]
      );

      renderTable(doc, "Person Debrief Info", flattenJSON(childDebrief.personDebriefInfo), ["Field", "Value"]);
      renderTable(doc, "Body Map Info", flattenJSON(childDebrief.bodyMapInfo), ["Field", "Value"]);
      renderTable(doc, "Person Comment Info", flattenJSON(childDebrief.personCommentInfo), ["Field", "Value"]);
      renderTable(doc, "Witness Data", flattenJSON(childDebrief.witnessData), ["Field", "Value"]);
    }

    // 3. Staff Debrief
    if (staffDebrief) {
      renderTable(
        doc,
        "Staff Debrief - General Info",
        [
          ["Task Name", staffDebrief.taskName],
          ["Selected Staff", staffDebrief.selectedStaff || "N/A"],
          ["Date", formatDateSafe(staffDebrief.updated_at)],
        ],
        ["Field", "Value"]
      );

      renderTable(doc, "Staff Debrief Info", flattenJSON(staffDebrief.staffDebriefInfo), ["Field", "Value"]);
      renderTable(doc, "Staff Member Info", flattenJSON(staffDebrief.staffMemberInfo), ["Field", "Value"]);
    }

    // 4. Support Statement
    if (supportStatement) {
      renderTable(
        doc,
        "Support Statement - General Info",
        [
          ["Task Name", supportStatement.taskName],
          ["Witness ID", supportStatement.witnessId],
          ["Date", formatDateSafe(supportStatement.updated_at)],
        ],
        ["Field", "Value"]
      );

      renderTable(doc, "Behaviour Records", flattenJSON(supportStatement.behaviorRecords), ["Field", "Value"]);
      renderTable(
        doc,
        "Temperature / Who Was Around",
        [
          ["Temperature", supportStatement.temperature],
          ["Who Was Around", supportStatement.whoWasAround],
        ],
        ["Field", "Value"]
      );
    }

    // Save PDF
    doc.save(`incident-summary-${formattedDate}.pdf`);
    setExportOpenSnackbar(true);
  };

  return (
    <Box>
      <Typography variant='subtitle1' sx={{ fontSize: "18px", fontWeight: "bold" }} align='left'>
        Incident Export
      </Typography>
      <Typography variant='body2' sx={{ fontSize: "14px", mb: 3 }} align='left'>
        Click below to export incident summary with all related records.
      </Typography>
      <Button
        variant='contained'
        color='primary'
        onClick={handleExportToPDF}
        sx={{ width: "100%", textTransform: "none" }}
      >
        Export
      </Button>
      <NotificationSnackbar
        open={openExportSnackbar}
        onClose={() => setExportOpenSnackbar(false)}
        color='success'
        message='Incident Report exported successfully.'
      />
    </Box>
  );
};

export default TasksExportIncident;
